<template>
	<div class="page">
		<PageBlocked v-if="isBlocked" :blockRole="blockRole" />
		<div v-else-if="isBlocked !== null">
			<v-skeleton-loader
				v-if="this.authIsLoading || this.settingsIsLoading"
				v-bind="attrs"
				type="card"
			/>
			<div v-else-if="this.profile">
				<v-card class="profile-preview-page__section pa-4" flat v-if="user && renewUrl">
					<v-card-text class="d-flex justify-space-between align-center">
						<p class="profile-full__label ma-0 mr-2 font-weight-bold">
							{{ renewLabel }}
						</p>
						<a :href="renewUrl" target="_blank" @click="handleRenewButtonClick">
							<v-btn
								:loading="false"
								border="false"
								elevation="0"
								id="profile-full__renew-button"
								class="profile-full__button profile-full__renew-button"
							>
								{{ renewButtonName }}
							</v-btn>
						</a>
					</v-card-text>
				</v-card>

				<v-card
					class="profile-preview-page__section pa-4"
					flat
					v-bind:style="{ paddingTop: userFlag ? '32px !important' : '16px' }"
				>
					<ProfileBadge v-if="userFlag" :badge="userFlag" />
					<div class="profile-preview-page__info-wrapper">
						<Avatar
							:avatar="avatarPath"
							size="120px"
							border-radius="square"
							class="profile-preview-page__avatar mr-4"
						/>

						<div class="profile-preview-page__info">
							<h1 class="profile-preview-page__info-username">
								{{ userName }}
							</h1>
							<div class="d-flex flex-column">
								<span
									v-if="companyName"
									class="profile-preview-page__info-company mb-1"
								>
									{{ companyName }}
								</span>
								<span
									v-if="heldPosition"
									class="profile-preview-page__info-position"
								>
									{{ heldPosition }}
								</span>
								<span v-if="location" class="profile-preview-page__info-location">
									<Icon
										type="pin"
										color="var(--location-color)"
										:size="14"
										:pr="4"
									/>
									<span>{{ location }}</span>
								</span>
							</div>
						</div>
					</div>

					<router-link :to="{ name: 'profile' }" @click.native="handleViewMyProfileClick">
						<v-btn
							:loading="false"
							border="false"
							elevation="0"
							class="profile-full__button mt-5"
						>
							View My Profile
						</v-btn>
					</router-link>
				</v-card>

				<div class="profile-preview-page__section-bookmarks-notes">
					<v-card class="profile-preview-page__section-bookmarks pa-7" flat>
						<router-link
							:to="{ name: 'profile-bookmarks' }"
							class="text-decoration-none"
						>
							<v-card-text
								class="d-flex align-center font-weight-bold justify-space-between"
							>
								<div class="d-flex align-center">
									<div class="mr-6 icon-base rounded-circle">
										<img :src="bookmarkIcon" alt="" />
									</div>
									<h2>Bookmarks</h2>
								</div>
								<h1 v-if="bookmarksMeta">{{ bookmarksMeta.totalCount }}</h1>
							</v-card-text>
						</router-link>
					</v-card>

					<v-card class="profile-preview-page__section-notes pa-7" flat>
						<router-link :to="{ name: 'profile-notes' }" class="text-decoration-none">
							<v-card-text
								class="d-flex align-center font-weight-bold justify-space-between"
							>
								<div class="d-flex align-center">
									<div class="mr-6 icon-base rounded-circle">
										<img :src="noteIcon" alt="" />
									</div>
									<h2>Notes</h2>
								</div>
								<h1 v-if="notesMeta">{{ notesMeta.totalCount }}</h1>
							</v-card-text>
						</router-link>
					</v-card>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import searchIcon from '@/assets/images/search.svg'
import bookmarkIcon from '@/assets/images/profile/bookmark.svg'
import noteIcon from '@/assets/images/profile/note.svg'
import Avatar from '@/components/Avatar'
import PageBlocked from '@/components/PageBlocked'
import Block from '@/mixins/Block.js'
import Dwh from '@/mixins/Dwh'
import ProfileBadge from '@/components/ProfileBadge.vue'

export default {
	name: 'ProfilePreviewPage',
	mixins: [Block, Dwh],
	components: {
		Avatar,
		PageBlocked,
		ProfileBadge,
	},
	computed: {
		...mapGetters('auth', {
			profile: 'profile',
			isAuthenticated: 'isAuthenticated',
			authIsLoading: 'isLoading',
		}),
		...mapGetters('settings', {
			user: 'user',
			appCore: 'appCore',
			settingsIsLoading: 'isLoading',
		}),
		...mapGetters('profile', ['notesMeta', 'bookmarksMeta', 'notes']),

		allowFields() {
			const { [this.profileFieldsType]: profileFields } = this.profile

			return profileFields.reduce((prev, current) => {
				return { ...prev, [current.field]: current }
			}, {})
		},
		renewLabel() {
			const { renew_area_text: value } = this.user || {}
			return value
		},
		renewButtonName() {
			const { renew_button_name: value } = this.user || {}
			return value
		},
		renewUrl() {
			const { renew_url: value } = this.user || {}
			return value
		},
		profileFieldsType() {
			if (!this.user) {
				return 'public'
			}

			const { view_profile_link: viewProfileLink } = this.user

			return this.getTypeFieldsFromUrl(viewProfileLink)
		},
		isVerticalTabs() {
			return this.$vuetify.breakpoint.mdAndUp
		},

		// short profile
		userFlag() {
			const { userFlag: flag } = this.profile || {}

			return flag
		},
		location() {
			const { location_profile_row: location = {} } = this.profile
			return location
		},
		userName() {
			const { visible_username: name } = this.profile
			return name
		},
		avatarPath() {
			const { user_logo: userLogo } = this.profile
			return userLogo
		},
		heldPosition() {
			const { third_profile_row: heldPosition } = this.profile
			return heldPosition
		},
		companyName() {
			const { second_profile_row: company } = this.profile
			return company
		},
		backButtonRoute() {
			return this.$routerHistory.previous().path
		},
	},
	methods: {
		...mapActions('resources', ['getCategory', 'getCategoryResources']),
		...mapActions('profile', ['getProfileNotes', 'getProfileBookmarks']),
		...mapActions('settings', ['getUserSettings']),

		onTabSwitch() {
			this.successMessage = false
			this.errorMessage = false
		},
		normalizeSettings(item) {
			const { setting, value } = item

			let settings = {}
			settings[setting.name] = value !== '0'

			return settings
		},
		dateCreated(originalDate) {
			return new Date(originalDate * 1000).toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'long',
				day: 'numeric',
			})
		},
		getTypeFieldsFromUrl(url) {
			const parts = url.split('/')
			return parts[parts.length - 1]
		},
		handleViewMyProfileClick() {
			this.sendTrigger({
				category: 'Profile',
				action: 'click_view_profile_button',
				module: 'Profile',
			})
		},
		handleRenewButtonClick() {
			this.sendTrigger({
				category: 'Profile',
				action: 'click_renew_button',
				module: 'Profile',
			})
		},
	},
	data() {
		return {
			searchIcon,
			bookmarkIcon,
			noteIcon,
			successMessage: false,
			errorMessage: false,
			attrs: {
				elevation: 0,
			},
			socialFieldNames: ['user_linkedin', 'user_facebook', 'user_twitter', 'user_custom_url'],
			phoneFieldNames: ['user_work_phone', 'user_cell_phone'],
			pageBlockedName: 'profile',
		}
	},
	beforeMount() {
		if (!this.user) {
			this.getUserSettings()
		}
		if (!this.isBlocked) {
			this.getProfileNotes()
			this.getProfileBookmarks()
		}
		if (
			this.$routerHistory._history
				.filter((path) =>
					['/profile/notes', '/profile/bookmarks'].every((item) => item !== path)
				)
				.indexOf('/profile-pre-view') === 0
		) {
			this.$routerHistory.reset()
		}
	},
}
</script>

<style lang="scss">
.v-list-item {
	padding: 0;

	.v-icon {
		margin-right: 12px;
	}
}

.profile-preview-page {
	font-family: Montserrat, sans-serif;
	margin-bottom: 30px;

	@media (max-width: 768px) {
		margin-bottom: 12px;
	}

	.v-card__text {
		color: rgba(0, 0, 0, 0.6) !important;
	}

	&__box {
		border-radius: 10px !important;
		max-width: 600px;
		width: 100%;

		@media (max-width: 480px) {
			&-title {
				font-size: 16px !important;
			}

			&-count-title {
				font-size: 21px !important;
			}
		}
	}

	&__user-info {
		@media (max-width: 480px) {
			flex-direction: column;

			&-avatar-wrapper {
				display: flex;
				justify-content: center;
				margin-bottom: 12px;
				padding-left: 40px;
			}
		}

		&-text {
			overflow: hidden;
			text-overflow: ellipsis;
			display: -webkit-box;
			-webkit-line-clamp: 1;
			-webkit-box-orient: vertical;
		}
	}

	&__section {
		max-width: 600px;
		border-radius: 10px !important;
		margin-bottom: 8px;

		&-bookmarks-notes {
			display: flex;
			column-gap: 8px;

			@media (max-width: 768px) {
				flex-direction: column;
			}
		}

		&-bookmarks,
		&-notes {
			width: 50%;
			font-family: Montserrat, sans-serif;
			border-radius: 10px !important;

			@media (max-width: 768px) {
				width: 100%;
				margin-bottom: 8px;
			}

			h1 {
				color: var(--accent-color);
				font-size: 24px;
				font-weight: 700;
				line-height: 29.26px;
			}

			h2 {
				color: var(--accent-color);
				font-size: 16px;
				font-weight: 700;
				line-height: 19.5px;
			}
		}
	}

	&__info {
		font-family: Montserrat, sans-serif;
		margin-bottom: 20px;
		width: 100%;

		&-wrapper {
			display: flex;
			flex-wrap: nowrap;

			@media (max-width: 480px) {
				flex-direction: column;
				align-items: center;
			}
		}

		&-username {
			font-size: 24px;
			line-height: 29.26px;
			font-weight: 700;
			color: var(--accent-color);
			margin-bottom: 12px;

			@media (max-width: 480px) {
				margin-top: 12px;
				text-align: center;
			}
		}

		&-company,
		&-position {
			font-size: 16px;
			line-height: 19.5px;
			color: #4a4a4a;
			width: 100%;
			overflow: hidden;
			text-overflow: ellipsis;
			display: -webkit-box;
			-webkit-line-clamp: 1;
			-webkit-box-orient: vertical;

			@media (max-width: 480px) {
				text-align: center;
			}
		}

		&-location {
			font-size: 13px;
			line-height: 19.37px;
			font-weight: 500;
			color: var(--location-color);
			display: flex;
			align-items: center;
			flex-wrap: nowrap;
			margin-top: 12px;

			@media (max-width: 480px) {
				justify-content: center;
			}
		}
	}
}

#profile-full__renew-button {
	background-color: var(--button-secondary-background-color) !important;
	color: var(--button-secondary-color) !important;
	text-transform: capitalize;
}

.profile-full {
	&__button {
		height: 42px !important;
		border-radius: 8px;
		font-size: 18px;
		line-height: 21.94px;
		font-weight: 700;
		text-transform: capitalize;
		font-family: Montserrat, sans-serif;
		padding: 0 38px !important;
		background-color: var(--button-secondary-background-color) !important;
		color: var(--button-secondary-color) !important;

		@media (max-width: 480px) {
			width: 100%;
		}
	}

	&__renew-button {
		width: fit-content;
	}

	&__label {
		font-size: 16px;
		font-weight: 700;
		line-height: 19.5px;
		color: var(--accent-color);
		font-family: Montserrat, sans-serif;
	}
}

.user {
	&__home-address,
	&__work-address {
		background: rgb(251, 251, 251);
		border-radius: 12px;
		border: 1px solid rgb(226, 226, 226);
	}

	&__brief-description {
		&__location {
			margin-left: -6px;

			&,
			&-icon {
				color: var(--location-color) !important;
			}
		}

		&__company,
		&__held-position {
			color: var(--company-position-large);
		}

		&__username {
			color: var(--username-category-title);
			word-break: break-word;
		}
	}

	&__phone-numbers > ul,
	&__networks > ul {
		list-style: none;
		padding: 0;
		margin: 0;
	}
}

.icon-base {
	width: 45px;
	height: 45px;
	background-color: var(--accent-color);
	position: relative;

	img {
		width: 50%;

		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
	}
}
</style>
